import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { AdminStoreItemResource, CreateStoreItemRequest } from '../types';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface StoreItemFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: AdminStoreItemResource | null;
  onSubmit: () => void;
}

const defaultForm: CreateStoreItemRequest = {
  key: '',
  name: '',
  description: '',
  sub_features: [],
  category: 'sales',
  price: 0,
  discount_percent: null,
  duration_type: 'lifetime',
  is_available_for_trial: false,
  trial_days: null,
  is_active: true,
  sort_order: 0,
  demo_url: null,
};

const StoreItemFormModal: React.FC<StoreItemFormModalProps> = ({ isOpen, onClose, item, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CreateStoreItemRequest>(defaultForm);
  const [newSubFeature, setNewSubFeature] = useState('');

  useEffect(() => {
    if (item) {
      setForm({
        key: item.key,
        name: item.name,
        description: item.description ?? '',
        sub_features: item.subFeatures ?? [],
        category: item.category,
        price: item.price,
        discount_percent: item.discountPercent ?? null,
        duration_type: item.durationType,
        is_available_for_trial: item.isAvailableForTrial,
        trial_days: item.trialDays ?? null,
        is_active: item.isActive,
        sort_order: item.sortOrder ?? 0,
        demo_url: item.demoUrl ?? null,
      });
    } else {
      setForm(defaultForm);
    }
    setError(null);
    setNewSubFeature('');
  }, [item, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (item) {
        await api.updateStoreItem(item.id, form);
      } else {
        await api.createStoreItem(form);
      }
      onSubmit();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const addSubFeature = () => {
    const trimmed = newSubFeature.trim();
    if (!trimmed) return;
    setForm(f => ({ ...f, sub_features: [...(f.sub_features ?? []), trimmed] }));
    setNewSubFeature('');
  };

  const removeSubFeature = (index: number) => {
    setForm(f => ({ ...f, sub_features: (f.sub_features ?? []).filter((_, i) => i !== index) }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-10">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 mb-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {item ? 'Edit Store Item' : 'Add Store Item'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Key <span className="text-red-500">*</span></label>
            <input
              type="text"
              required
              value={form.key}
              onChange={e => setForm(f => ({ ...f, key: e.target.value }))}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g. advance_printer"
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              required
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={form.description ?? ''}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Category + Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Category <span className="text-red-500">*</span></label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value as 'sales' | 'other' }))}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="sales">Sales</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Duration Type <span className="text-red-500">*</span></label>
              <select
                value={form.duration_type}
                onChange={e => setForm(f => ({ ...f, duration_type: e.target.value as 'lifetime' | 'subscription' }))}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="lifetime">Lifetime</option>
                <option value="subscription">Subscription</option>
              </select>
            </div>
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Price (Rp) <span className="text-red-500">*</span></label>
            <input
              type="number"
              required
              min={0}
              value={form.price}
              onChange={e => setForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Discount % + Sort Order */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Discount %</label>
              <input
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={form.discount_percent ?? ''}
                onChange={e => setForm(f => ({ ...f, discount_percent: e.target.value ? parseFloat(e.target.value) : null }))}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Sort Order</label>
              <input
                type="number"
                min={0}
                value={form.sort_order ?? 0}
                onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                className="h-4 w-4 text-primary-600 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Active</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_available_for_trial}
                onChange={e => setForm(f => ({ ...f, is_available_for_trial: e.target.checked, trial_days: e.target.checked ? f.trial_days : null }))}
                className="h-4 w-4 text-primary-600 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Available for Trial</span>
            </label>
          </div>

          {/* Trial Days — only when trial enabled */}
          {form.is_available_for_trial && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Trial Days <span className="text-red-500">*</span></label>
              <input
                type="number"
                required
                min={1}
                value={form.trial_days ?? ''}
                onChange={e => setForm(f => ({ ...f, trial_days: parseInt(e.target.value) || null }))}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}

          {/* Sub Features */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sub Features</label>
            <div className="space-y-2">
              {(form.sub_features ?? []).map((sf, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded px-2 py-1">{sf}</span>
                  <button type="button" onClick={() => removeSubFeature(idx)} className="text-red-400 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSubFeature}
                  onChange={e => setNewSubFeature(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSubFeature(); } }}
                  placeholder="Add sub-feature..."
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={addSubFeature}
                  className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm hover:bg-gray-200"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Demo URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Demo URL</label>
            <input
              type="text"
              value={form.demo_url ?? ''}
              onChange={e => setForm(f => ({ ...f, demo_url: e.target.value || null }))}
              placeholder="https://youtube.com/..."
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-400">YouTube URL for demo video. Leave empty to hide the demo button.</p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <LoadingSpinner size="sm" />}
              {item ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StoreItemFormModal;
